/**
 * THE revert switch for the attached Tools sidebar (Hamzah approved the mock
 * 2026-08-23, explicitly asking for an easy way back).
 *
 * true  — the Tools panel renders as a full-height column fused to the app
 *         sidebar: part of the page chrome, hairline-separated, collapsible
 *         to a slim icon rail.
 * false — the pre-existing floating card over the canvas, exactly as before.
 *
 * Both layouts are kept fully intact in VerticalToolbar/MockupCanvasKonva —
 * flipping this ONE boolean is the whole revert.
 */
export const ATTACHED_TOOLS_SIDEBAR = true;

/**
 * Ship gates / launch kill-switches for the light categories (Hamzah: "feature
 * flag the different kinds of lighting, so I can toggle them on/off").
 *
 * Each category is an env-backed build-time flag defaulting ON (the categories
 * are complete + verified; the flags exist to pull one at launch). The shape and
 * export are unchanged, so every existing consumer keeps working — only the
 * source of the booleans moved from hardcoded `true` to env:
 *
 *   VITE_ENABLE_HOLIDAY_LIGHTING    — holiday strings/icicles/ground stakes
 *   VITE_ENABLE_PERMANENT_LIGHTING  — year-round downlight/uplight/track/glow
 *   VITE_ENABLE_LANDSCAPE_LIGHTING  — garden wall/flowerbed/puck fixtures
 *
 * Semantics (present-and-not-'false' = ON): set a var to the string 'false' to
 * disable that category; unset, 'true', or any other value leaves it on. Vite
 * inlines env at build time, so flipping one requires a redeploy.
 *
 * RUNTIME COMPOSITION: these build-time flags are now the DEFAULTS beneath
 * runtime super-admin toggles. src/features/featureToggles resolves each as
 * `runtimeToggle ?? envDefault` (system_settings keys
 * `feature_{holiday,permanent,landscape}_lighting_enabled`), and the editor mount
 * (MockupCanvasKonva) resolves them into this SAME shape and passes them down to
 * the toolbar + creation boundary. This const stays the fallback used while the
 * runtime value loads / when absent, and renderers still never read it — saved
 * runs always render.
 *
 * SCOPE — what a disabled category does and does NOT touch. A disabled category
 * disappears from the Tools dock (no category pill, no options) and the
 * placement boundary refuses to CREATE new runs of its types — this is purely an
 * AUTHORING gate. Runs of its types in EXISTING designs keep rendering
 * untouched on every path (canvas, PNG export, animation frames, scene photos,
 * gallery, build sheet): the renderers dispatch on each run's `light.type` and
 * never read these flags. The two consumers are the toolbar
 * (components/VerticalToolbar.tsx) and the creation boundary
 * (hooks/useMouseHandlers.ts), both via utils/mockupLightCategories helpers.
 * `normalizeLightType` runs ONLY on the active tool selection, never on saved
 * run data at load — a saved landscape run is never silently converted.
 */
export const LIGHT_CATEGORY_FLAGS = {
  holiday: import.meta.env.VITE_ENABLE_HOLIDAY_LIGHTING !== 'false',
  permanent: import.meta.env.VITE_ENABLE_PERMANENT_LIGHTING !== 'false',
  landscape: import.meta.env.VITE_ENABLE_LANDSCAPE_LIGHTING !== 'false',
} as const;
